import { coins } from '@klarpay/enums'
import { AdapterName, AdapterState } from '@tronweb3/tronwallet-abstract-adapter'
import { useWallet } from '@tronweb3/tronwallet-adapter-react-hooks'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Chain, erc20ABI } from 'wagmi'
import { TronWeb } from 'tronweb'
import { BalanceResult, BlockchainAdapter, BlockchainType, EMPTY_BALANCE } from './types'

const isProduction = import.meta.env.MODE === 'production'

export const TRON_MAINNNET_CHAIN_ID = 0x2b6653dc
export const TRON_SHASTA_CHAIN_ID = 0x94a9059e

const TRON_CHAIN: Chain = {
  id: isProduction ? TRON_MAINNNET_CHAIN_ID : TRON_SHASTA_CHAIN_ID,
  name: 'Tron',
  network: isProduction ? 'tron' : 'shasta',
  nativeCurrency: {
    name: 'TRX',
    symbol: 'TRX',
    decimals: 6,
  },
  rpcUrls: {
    default: {
      http: isProduction ? ['https://api.trongrid.io'] : ['https://api.shasta.trongrid.io'],
    },
    public: {
      http: isProduction ? ['https://api.trongrid.io'] : ['https://api.shasta.trongrid.io'],
    },
  },
}

export function useTronAdapter(): BlockchainAdapter {
  const {
    address,
    connected,
    connecting,
    select,
    disconnect: adapaterDisconnect,
    connect: connectAdapter,
    signMessage,
    signTransaction,
    wallet,
    wallets,
  } = useWallet()
  const [connector, setConnector] = useState<string>()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tronWeb = (window as any).tronWeb as TronWeb | undefined

  const disconnect = useCallback(() => {
    adapaterDisconnect()
    if (!wallet?.adapter.name) {
      return
    }
    localStorage.setItem(wallet.adapter.name + '.safe.disconnect', 'true')
  }, [wallet?.adapter.name])

  // Send TRC20 transaction: simulate, sign, send, return hash
  const sendTrc20Transaction = useCallback(
    async (contractAddress: string, amount: number, toAddress: string) => {
      if (!wallet || !address || !tronWeb) throw new Error('Wallet not connected')

      const { abi } = await tronWeb.trx.getContract(contractAddress)
      const contract = tronWeb.contract(abi.entrys, contractAddress)

      // Get token decimals and adjust amount
      const decimals = Number(await contract.methods.decimals().call())
      const adjustedAmount = amount * Math.pow(10, decimals)

      const tx = await tronWeb.transactionBuilder.triggerSmartContract(
        contractAddress,
        'transfer(address,uint256)',
        {},
        [
          { type: 'address', value: toAddress },
          { type: 'uint256', value: adjustedAmount },
        ],
      )
      const signedTx = await signTransaction(tx.transaction)
      const result = await tronWeb.trx.sendRawTransaction(signedTx)

      return result.txid
    },
    [wallet, address, signTransaction],
  )

  // Send TRX transaction
  const sendTrxTransaction = useCallback(
    async (amount: number, toAddress: string) => {
      if (!wallet || !address) throw new Error('Wallet not connected')
      if (!tronWeb) throw new Error('TronWeb not found')

      // 1 TRX = 1,000,000 SUN
      const sunAmount = Math.floor(amount * 1_000_000)

      const tx = await tronWeb.transactionBuilder.sendTrx(toAddress, sunAmount)
      const signedTx = await signTransaction(tx)
      const result = await tronWeb.trx.sendRawTransaction(signedTx)
      return result.txid
    },
    [wallet, address, signTransaction],
  )

  const connect = useCallback(
    (connector: string) => {
      setConnector(connector)
      localStorage.removeItem(connector + '.safe.disconnect')
      const wallet = wallets.find((w) => w.adapter.name === connector)
      if (wallet?.adapter.state === AdapterState.NotFound) {
        toast.warn(`${wallet.adapter.name} extention is not installed in your browser.`)
        return
      }
      if (wallet?.state === AdapterState.Disconnect) {
        connectAdapter()
      }
      select(connector as AdapterName)
    },
    [wallets, select, wallet?.adapter.state, wallet?.state],
  )

  const reconnect = useCallback(async () => {
    if (wallet && wallet.state === AdapterState.Disconnect) {
      await connectAdapter()
    }
  }, [wallet?.state, connectAdapter])

  const tronTokens = useMemo(
    () =>
      Object.entries(coins)
        .flatMap(([symbol, coin]) =>
          Object.entries(coin.chains).map(([chain, chainData]) => ({
            ...chainData,
            symbol,
            chain,
          })),
        )
        .filter((c) => c.chain === TRON_CHAIN.network),
    [],
  )

  const getBalances = useCallback(
    async (contracts: string[]) => {
      return Promise.all(
        contracts.map(
          async (token): Promise<BalanceResult> => {
            const tokenSpec = tronTokens.find((c) => c.contract === token)
            try {
              if (!tokenSpec) {
                throw new Error(`Unknown tron contract ${token}`)
              }
              if (!connected || connecting || !tronWeb || !address) {
                return { ...EMPTY_BALANCE, symbol: tokenSpec.symbol }
              }
              if (token === 'native') {
                const balance = await tronWeb.trx.getBalance(address)
                return {
                  value: balance,
                  decimals: tokenSpec.decimals,
                  symbol: tokenSpec.symbol,
                  token: 'native',
                  formatted: (Number(balance) / Math.pow(10, tokenSpec.decimals)).toFixed(6),
                }
              }
              const contract = tronWeb.contract(erc20ABI, token)
              const balance = await contract.balanceOf(address).call()

              const formatted = (Number(balance) / Math.pow(10, tokenSpec.decimals)).toFixed(
                tokenSpec.decimals,
              )

              return {
                token,
                formatted,
                value: BigInt(balance.toString()),
                decimals: Number(tokenSpec.decimals),
                symbol: tokenSpec.symbol,
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error(e)
              return { ...EMPTY_BALANCE, symbol: tokenSpec?.symbol ?? '-' }
            }
          },
        ),
      )
    },
    [connected, connecting, tronWeb, tronTokens],
  )

  const signMessageCallback = useCallback(
    async (message: string) => {
      await reconnect()
      return await signMessage(message)
    },
    [reconnect, signMessage, connectAdapter],
  )

  return {
    address: address as `T${string}`,
    hasPublicClient: false,
    isConnected:
      connected &&
      !connecting &&
      localStorage.getItem(wallet?.adapter.name + '.safe.disconnect') !== 'true',
    chain: TRON_CHAIN,
    connect,
    disconnect,
    signMessage: signMessageCallback,
    getBalances,
    type: BlockchainType.TRON,
    walletType: connector,
    sendTransaction: (contract, amount, to) => {
      if (contract === 'native') {
        return sendTrxTransaction(amount, to)
      } else {
        return sendTrc20Transaction(contract, amount, to)
      }
    },
  }
}
